<template>
  <div data-structure="condition-set">

    <template v-if="data.query">
      <form v-on:submit.prevent="searchConditionSet">
        <div class="input-search mb-2">
          <div class="input-search__input-outer">
            <input class="input-search__input" type="text" v-model="conditionSetSearch" v-on:keyup="debounce" v-on:keyup.enter="searchConditionSet"
              placeholder="Filter by unit name or code" aria-label="Filter units by name or code" />
            <button class="input-search__btn-clear" type="button" v-if="conditionSetSearch !== ''" v-on:click="clearSearch">
              <span class="input-search__icon far fa-times"></span>
            </button>
            <button class="input-search__btn-search" type="button" :disabled="conditionSetSearch == '' || loading" v-on:click="searchConditionSet">
              <span class="input-search__icon far fa-search"></span>
            </button>
          </div>
        </div>
        <div v-if="unitLevels.length || isTransnational" class="filter-options d-flex justify-content-between mb-3">
          <div class="filter-level d-flex align-items-center">
            <button v-for="unit in unitLevels" :key="unit+'00'" class="btn btn-sm btn-tertiary" v-bind:class="{ active: unitLevelFilters.includes(unit) }"
              type="button"
              v-on:click.prevent="applyUnitFilters(unit)">Level
              {{unit}}00</button>
          </div>
          <div class="reordering d-flex align-items-center">
            <div class="custom-control custom-checkbox" v-if="isTransnational">
              <input type="checkbox" v-model="filterLocation" id="filter-location-checkbox" class="custom-control-input"
                v-on:change="applyLocationFilter()">
              <label class="custom-control-label text-left" for="filter-location-checkbox">
                Show units from my location only
              </label>
            </div>
            <button type="button" class="btn btn-link reorder-units" :id="'tooltip-reordering-units'" v-on:click.prevent="changeSortMethod">
              <i class="fa fa-sort-alt"></i>
            </button>
          </div>
        </div>
      </form>
    </template>

    <template v-if="loading">
      <div class="page-content-state page-content-state-loading">
        <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
        <div class="page-content-state__content">Loading units</div>
      </div>
    </template>

    <template v-else>
      <!-- Selection of Units (Relationships) -->
      <ul v-if="pagedResultItems && pagedResultItems.length">
        <li v-for="relationship in pagedResultItems" :key="relationship.id">
          <SlideOutRelationship :relationship="relationship" :location="location+'>'+relationship.code"
            :mandatory="checkMandatory(relationship)" :parentLocation="location" :virtualRelationship="true" :firstLevelTitle="firstLevelTitle"
            :verticalGrouping="verticalGrouping" />
        </li>
      </ul>
      <template v-else>
        <div class="page-content-state" v-if="!error">
          <span class="page-content-state__icon fal fa-sad-cry"></span>
          <div class="page-content-state__header">No units found.</div>
          <div class="page-content-state__content">Please try another search.</div>
        </div>
      </template>
    </template>

    <template v-if="data.query">
      <b-pagination class="condition-set__pagination"
        v-model="conditionSetPage"
        :total-rows="this.filteredConditionSetItems.length"
        :per-page="rowsPerPage"
        :disabled="loading"
        v-on:change="loadPage"
        first-number
        last-number>
      </b-pagination>
    </template>

    <template v-if="error && !loading">
      <div class="page-content-state">
        <span class="page-content-state__icon  fal fa-sad-cry"></span>
        <div class="page-content-state__header">Sorry, there was a problem loading options.</div>
        <button type="button" class="btn btn-primary mt-3" v-on:click="clearSearch()">Try again</button>
      </div>
    </template>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { dynamicSort } from 'common/helpers/dynamic-sort.js'
import SlideOutRelationship from './SlideOutRelationship.vue'
import Curriculum from 'client/js/models/curriculum'

export default {
  components: {
    SlideOutRelationship
  },
  data: function () {
    return {
      loading: false,
      conditionSetSearch: '',
      cachedConditionSetItems: [],
      filteredConditionSetItems: [],
      pagedResultItems: [],
      conditionSetPage: 1,
      rowsPerPage: 10,
      showSearch: false,
      error: null,
      unitLevels: [],
      unitLevelFilters: [],
      sortProperty: '',
      filterLocation: false
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    requiredCredits: {
      type: Number,
      required: true
    },
    location: {
      type: String,
      required: false
    },
    mandatory: {
      type: Boolean,
      required: false,
      default: false
    },
    firstLevelTitle: {
      type: String,
      required: true
    },
    verticalGrouping: {
      type: String,
      require: true
    },
    containerId: {
      type: String
    }
  },
  computed: {
    ...mapGetters({
      selectedPreferences: 'preferences/getSelectedStudyPreferences',

      selectedItems: 'units/getSelectedCurriculumItems',
      getUnitLevel: 'units/getUnitLevel',
      includeDraftVersions: 'courses/includeDraftVersions'
    }),
    isTransnational() {
      return this?.selectedPreferences?.studentType === 'Transnational';
    },
    transnationalLocation() {
      return this?.selectedPreferences?.placeOfResidence;
    }
  },
  methods: {
    updateCredits(c) {
      this.credits = this.credits + c
    },
    checkMandatory(data) {
      if (this.mandatory && data.connector == 'AND') {
        return true
      } else {
        return false
      }
    },
    debounce: function () {
      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.searchConditionSet()
      }, 1000)
    },
    getConditionSet: async function () {
      if (this.data.query) {
        this.loading = true
        this.error = null
        try {
          const response = await Curriculum.getCachedConditionSet(this.containerId, this.includeDraftVersions)
          this.cachedConditionSetItems = response.items.sort(dynamicSort('code'))
          this.filteredConditionSetItems = this.cachedConditionSetItems
          this.conditionSetPage = 1
        } catch (err) {
          this.error = err
        }
        this.loading = false
        this.loadUnitFilters(this.filteredConditionSetItems)
        this.loadPage(this.conditionSetPage)
      }
    },
    searchConditionSet: async function () {
      this.loading = true
      this.error = null
      try {
        if (this.conditionSetSearch || this.unitLevelFilters.length || this.filterLocation) {
          const searchTerms = this.conditionSetSearch.split(' ')
          this.filteredConditionSetItems = this.cachedConditionSetItems.filter(
            (item) =>
              searchTerms.every((x) => item.name.toLowerCase().includes(x.toLowerCase())) ||
              item.code.toLowerCase().includes(this.conditionSetSearch.toLowerCase())
          )

          this.loadUnitFilters(this.filteredConditionSetItems)
          if (this.unitLevelFilters.length) {
            this.filteredConditionSetItems = this.filteredConditionSetItems.filter((item) =>
              this.unitLevelFilters.some((x) => this.getUnitLevel(item.code).includes(x))
            )
          }

          if (this.filterLocation) {
            this.filteredConditionSetItems = this.filteredConditionSetItems.filter((item) =>
              item.raw.offerings.some((x) => x.location.id === this.transnationalLocation) === true
            )
          }

          this.sortItemSet()
          this.conditionSetPage = 1
          this.loadPage(this.conditionSetPage)
        } else {
          this.clearSearch()
        }
      } catch (err) {
        this.error = err
      }
      this.loading = false
    },
    loadUnitFilters: async function (itemSet) {
      this.unitLevels = []
      itemSet?.forEach((unit) => {
        let unitLevel = this.getUnitLevel(unit.code)
        if (unitLevel != 0)
          if (!this.unitLevels.includes(unitLevel)) {
            this.unitLevels.push(unitLevel)
          }
      })
      this.unitLevels.sort()
    },
    applyLocationFilter: async function () {
      this.conditionSetPage = 1
      await this.searchConditionSet()
    },
    applyUnitFilters: async function (unit) {
      if (!this.unitLevelFilters.includes(unit)) {
        this.unitLevelFilters.push(unit)
      } else {
        this.unitLevelFilters.splice(this.unitLevelFilters.indexOf(unit), 1)
      }
      this.conditionSetPage = 1
      await this.searchConditionSet()
    },
    loadPage: async function (page) {
      this.setPagedResults(page, this.filteredConditionSetItems)
      this.conditionSetPage = page
    },
    changeSortMethod: function () {
      if (this.sortProperty === '-code') {
        this.sortProperty = 'code'
      } else {
        this.sortProperty = '-code'
      }
      this.sortItemSet()
    },
    sortItemSet: function () {
      this.filteredConditionSetItems = this.filteredConditionSetItems.sort(dynamicSort(this.sortProperty))
      this.conditionSetPage = 1
      this.loadPage(this.conditionSetPage)
    },
    setPagedResults(page, items, reset = false) {
      if (reset) {
        this.filteredConditionSetItems = this.cachedConditionSetItems
      }
      this.pagedResultItems = items?.slice((page - 1) * this.rowsPerPage, (page - 1) * this.rowsPerPage + this.rowsPerPage)
    },
    clearSearch() {
      this.unitLevelFilters = []
      this.conditionSetSearch = ''
      this.filteredConditionSetItems = this.cachedConditionSetItems
      this.conditionSetPage = 1
      this.loadUnitFilters(this.filteredConditionSetItems)
      this.loadPage(this.conditionSetPage)
    }
  },
  mounted: async function () {
    await this.getConditionSet()
  },
  watch: {}
}
</script>
